import React from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Stack,
	Typography,
	BoxProps,
	IconButton,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	RiCheckboxCircleLine,
	RiErrorWarningLine,
	RiInformationLine,
	RiAlertLine,
	RiCloseLine,
} from 'react-icons/ri';
import { IconType } from 'react-icons';

export interface BannerProps extends Omit<BoxProps, 'className'> {
	variant: 'info' | 'warning' | 'danger' | 'success';
	title?: string;
	description?: string | React.ReactNode;
	icon?: IconType;
	raised?: boolean;
	button?: {
		text: string;
		onClick: () => void;
		loading?: boolean;
	};
	buttonEl?: React.ReactNode;
	onClose?: () => void;
}

const IconByVariant: Record<BannerProps['variant'], IconType> = {
	info: RiInformationLine,
	warning: RiAlertLine,
	danger: RiErrorWarningLine,
	success: RiCheckboxCircleLine,
};

const Banner = (props: BannerProps) => {
	const {
		variant,
		title,
		description,
		icon,
		button,
		buttonEl,
		onClose,
		raised,
		...boxProps
	} = props;
	const classes = useStyles(props);

	const Icon = icon ?? IconByVariant[variant];

	return (
		<Box className={classes.wrapper} {...boxProps}>
			<Stack direction="column" spacing={1}>
				<Stack direction="row" justifyContent="flex-start" alignItems="center">
					<Box className={classes.iconWrapper}>
						<Icon color="inherit" />
					</Box>
					<Typography
						variant="h6"
						sx={{ flex: 1, mr: 2 }}
						className={!!description ? classes.title : classes.description}
					>
						{title}
					</Typography>
					{!!button && (
						<Box className={classes.buttonWrapper}>
							<Button onClick={button.onClick} color="inherit" className={classes.button}>
								{button.loading ? <CircularProgress size={20} color="inherit" /> : button.text}
							</Button>
						</Box>
					)}
					{!!buttonEl && buttonEl}
					{!!onClose && (
						<IconButton>
							<RiCloseLine />
						</IconButton>
					)}
				</Stack>
				{!!description && (
					<Stack direction="row">
						<Box className={classes.iconWrapper} />
						{typeof description === 'string' ? (
							<Typography variant="body2" className={classes.description}>
								{description}
							</Typography>
						) : (
							description
						)}
					</Stack>
				)}
			</Stack>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		wrapper: {
			padding: theme.spacing(2),
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'stretch',
			borderRadius: 4,
			boxShadow: (props: BannerProps) => (props.raised ? '0 1px 10px 0 rgba(0,0,0,0.35)' : 'none'),
			backgroundColor: (props: BannerProps) => {
				switch (props.variant) {
					case 'info':
						return theme.palette.info.lightest;
					case 'warning':
						return theme.palette.warning.lightest;
					case 'danger':
						return theme.palette.error.lightest;
					case 'success':
						return theme.palette.success.lightest;
					default:
						return undefined;
				}
			},
		},
		iconWrapper: {
			paddingRight: theme.spacing(2),
			minWidth: 40,
			maxWidth: 40,
			color: (props: BannerProps) => {
				switch (props.variant) {
					case 'info':
						return theme.palette.info.main;
					case 'warning':
						return theme.palette.warning.main;
					case 'danger':
						return theme.palette.error.main;
					case 'success':
						return theme.palette.success.main;
					default:
						return undefined;
				}
			},
		},

		title: {
			color: theme.palette.text.primary,
			fontWeight: 500,
			fontSize: 16,
		},
		description: {
			color: theme.palette.text.secondary,
			fontWeight: 400,
			fontSize: 14,
		},
		buttonWrapper: {
			color: (props: BannerProps) => {
				switch (props.variant) {
					case 'info':
						return theme.palette.text.primary;
					case 'warning':
						return theme.palette.text.primary;
					case 'danger':
						return theme.palette.text.primary;
					default:
						return undefined;
				}
			},
			[theme.breakpoints.up('md')]: {
				marginTop: theme.spacing(0),
				marginLeft: theme.spacing(2),
			},
		},
		button: {
			fontWeight: 600,
		},
	}),
);

export default Banner;
